import React, { useState } from "react";
import { alumnosProfesorService } from "../../../services/profesor/alumnos.service";
import { AlumnoProfesor, UpdateAlumnoEdit } from "../alumnos/dto/alumnos.dto";

interface AlumnosTableProps {
  alumnos: AlumnoProfesor[];
  loading?: boolean;
  reloadAlumnos: () => void;
}

interface NotificationState {
  isOpen: boolean;
  type: "success" | "error" | "warning" | "info";
  title: string;
  message: string;
}

export const AlumnosTable: React.FC<AlumnosTableProps> = ({
  alumnos,
  loading = false,
  reloadAlumnos,
}) => {
  const [selectedAlumno, setSelectedAlumno] = useState<AlumnoProfesor | null>(
    null
  );
  const [modalAbierto, setModalAbierto] = useState(false);
  const [accion, setAccion] = useState<"editar" | "eliminar" | "crear" | null>(
    null
  );
  const [nuevoAlumno, setNuevoAlumno] = useState({
    nombres: "",
    apellidos: "",
    email: "",
    rut: "",
    cursoId: null as number | null,
    anioEscolarId: null as number | null,
    vigente: true,
  });
  const [notification, setNotification] = useState<NotificationState>({
    isOpen: false,
    type: "success",
    title: "",
    message: "",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [aniosEscolares, setAniosEscolares] = useState<
    { idAnioEscolar: number; nombre: string; orden: number }[]
  >([]);
  const [cursos, setCursos] = useState<
    {
      idCurso: number;
      nombre: string;
      anio: number;
      anioEscolarId: number;
      AnioEscolar: {
        idAnioEscolar: number;
        nombre: string;
        orden: number;
      };
    }[]
  >([]);
  const [selectedCursoId, setSelectedCursoId] = useState<number | null>(null);
  const [selectedAnioEscolarId, setSelectedAnioEscolarId] = useState<
    number | null
  >(null);
  const [alumnoCreado, setAlumnoCreado] = useState<{
    nombres: string;
    apellidos: string;
    email: string;
    passwordTemporal: string;
  } | null>(null);
  const [nuevaPassword, setNuevaPassword] = useState("");
  const [confirmarPassword, setConfirmarPassword] = useState("");
  const [mostrarCambiarPassword, setMostrarCambiarPassword] = useState(false);

  const showNotification = (
    type: "success" | "error" | "warning" | "info",
    title: string,
    message: string
  ) => {
    setNotification({ isOpen: true, type, title, message });
    setTimeout(() => {
      setNotification({ isOpen: false, type, title: "", message: "" });
    }, 4000);
  };

  const getErrorMessage = (error: any): { title: string; message: string } => {
    if (error?.response?.data) {
      const errorData = error.response.data;

      if (errorData.error) {
        return {
          title: "Error",
          message: errorData.error,
        };
      }

      if (errorData.message) {
        return {
          title: "Error",
          message: errorData.message,
        };
      }

      if (errorData.detalles && Array.isArray(errorData.detalles)) {
        const detalles = errorData.detalles
          .map((d: any) => {
            if (typeof d === "string") return d;
            if (d.message) return d.message;
            if (d.path && d.message) return `${d.path.join(".")}: ${d.message}`;
            return JSON.stringify(d);
          })
          .join(", ");
        return {
          title: "Error de validación",
          message: detalles || "Los datos ingresados no son válidos.",
        };
      }
    }

    if (
      error?.message?.includes("Network Error") ||
      error?.code === "ERR_NETWORK"
    ) {
      return {
        title: "Error de conexión",
        message:
          "No se pudo conectar con el servidor. Verifica tu conexión a internet.",
      };
    }

    if (error?.code === "ECONNABORTED" || error?.message?.includes("timeout")) {
      return {
        title: "Tiempo de espera agotado",
        message: "La solicitud tardó demasiado. Por favor, intenta nuevamente.",
      };
    }

    if (error?.response?.status === 400) {
      return {
        title: "Datos inválidos",
        message:
          error?.response?.data?.error || "Los datos enviados no son válidos.",
      };
    }

    if (error?.response?.status === 401) {
      return {
        title: "No autorizado",
        message: "Tu sesión ha expirado. Por favor, inicia sesión nuevamente.",
      };
    }

    if (error?.response?.status === 403) {
      return {
        title: "Acceso denegado",
        message: "No tienes permisos para realizar esta acción.",
      };
    }

    if (error?.response?.status === 404) {
      return {
        title: "No encontrado",
        message:
          error?.response?.data?.error || "El recurso solicitado no existe.",
      };
    }

    if (error?.response?.status === 409) {
      return {
        title: "Conflicto",
        message:
          error?.response?.data?.error ||
          "Ya existe un registro con estos datos.",
      };
    }

    if (error?.response?.status === 422) {
      return {
        title: "Error de validación",
        message:
          error?.response?.data?.error ||
          "Los datos no cumplen con los requisitos.",
      };
    }

    if (error?.response?.status === 500) {
      return {
        title: "Error del servidor",
        message:
          "Ocurrió un error en el servidor. Por favor, intenta más tarde.",
      };
    }

    if (error?.message) {
      return {
        title: "Error",
        message: error.message,
      };
    }
    return {
      title: "Error inesperado",
      message:
        "Ocurrió un error inesperado. Por favor, intenta nuevamente o contacta al soporte.",
    };
  };

  const abrirModal = async (
    alumno: AlumnoProfesor,
    tipo: "editar" | "eliminar"
  ) => {
    setSelectedAlumno(alumno);
    setAccion(tipo);
    setModalAbierto(true);
    setMostrarCambiarPassword(false);
    setNuevaPassword("");
    setConfirmarPassword("");

    if (tipo === "editar") {
      try {
        const [aniosData, cursosData] = await Promise.all([
          alumnosProfesorService.getAniosEscolares(),
          alumnosProfesorService.getCursosByColegio(),
        ]);
        setAniosEscolares(aniosData);
        setCursos(cursosData);

        let anioEscolarIdEncontrado: number | null = null;
        if (alumno.cursos?.[0]?.anioEscolar) {
          const anioEscolarEncontrado = aniosData.find(
            (a) => a.nombre === alumno.cursos[0].anioEscolar
          );
          if (anioEscolarEncontrado) {
            anioEscolarIdEncontrado = anioEscolarEncontrado.idAnioEscolar;
            setSelectedAnioEscolarId(anioEscolarIdEncontrado);
          }
        }

        if (alumno.cursos?.[0]?.idCurso) {
          const cursoActual = cursosData.find(
            (c) => c.idCurso === alumno.cursos[0].idCurso
          );
          if (cursoActual) {
            setSelectedCursoId(cursoActual.idCurso);
            if (!anioEscolarIdEncontrado) {
              setSelectedAnioEscolarId(cursoActual.anioEscolarId);
            }
          } else {
            setSelectedCursoId(null);
          }
        } else {
          setSelectedCursoId(null);
        }
      } catch (error: any) {
        console.error("Error cargando opciones:", error);
        const errorInfo = getErrorMessage(error);
        showNotification("error", errorInfo.title, errorInfo.message);
      }
    }
  };

  const abrirModalCrear = async () => {
    setSelectedAlumno(null); // Asegurar que selectedAlumno sea null
    setAccion("crear");
    setModalAbierto(true);
    setNuevoAlumno({
      nombres: "",
      apellidos: "",
      email: "",
      rut: "",
      cursoId: null,
      anioEscolarId: null,
      vigente: true,
    });
    setSelectedCursoId(null);
    setSelectedAnioEscolarId(null);
    try {
      const [aniosData, cursosData] = await Promise.all([
        alumnosProfesorService.getAniosEscolares(),
        alumnosProfesorService.getCursosByColegio(),
      ]);
      setAniosEscolares(aniosData);
      setCursos(cursosData);
    } catch (error: any) {
      console.error("Error cargando opciones:", error);
      const errorInfo = getErrorMessage(error);
      showNotification("error", errorInfo.title, errorInfo.message);
    }
  };

  const cerrarModal = () => {
    setModalAbierto(false);
    setSelectedAlumno(null);
    setAccion(null);
    setSelectedCursoId(null);
    setSelectedAnioEscolarId(null);
    setNuevaPassword("");
    setConfirmarPassword("");
    setMostrarCambiarPassword(false);
    setNuevoAlumno({
      nombres: "",
      apellidos: "",
      email: "",
      rut: "",
      cursoId: null,
      anioEscolarId: null,
      vigente: true,
    });
  };

  const copiarContrasena = () => {
    if (alumnoCreado?.passwordTemporal) {
      navigator.clipboard.writeText(alumnoCreado.passwordTemporal);
      showNotification(
        "success",
        "Contraseña copiada",
        "La contraseña se ha copiado al portapapeles."
      );
    }
  };

  const cerrarModalContrasena = () => {
    setAlumnoCreado(null);
    showNotification(
      "success",
      "Alumno creado",
      "El alumno ha sido registrado correctamente."
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="mb-4 flex justify-end">
        <button
          onClick={abrirModalCrear}
          className="px-4 py-2 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-sm hover:shadow-md flex items-center gap-2 font-medium"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 4v16m8-8H4"
            />
          </svg>
          Crear Alumno
        </button>
      </div>

      <div className="w-full min-w-max overflow-x-auto">
        <table
          className="w-full divide-y divide-gray-200"
          style={{ minWidth: "1600px" }}
        >
          <thead className="bg-gradient-to-r from-purple-50 to-purple-100">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">
                Nombre
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">
                Apellidos
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">
                Rut
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">
                Año Escolar
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">
                Curso
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">
                Colegio
              </th>
              <th className="px-6 py-4 text-left text-xs font-bold text-purple-700 uppercase tracking-wider">
                Vigencia
              </th>
              <th className="px-6 py-4 text-center text-xs font-bold text-purple-700 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-200">
            {alumnos.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-6 py-12 text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-purple-100 mb-4">
                    <svg
                      className="w-8 h-8 text-purple-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                      />
                    </svg>
                  </div>
                  <p className="text-gray-500 text-lg font-medium">
                    No tienes alumnos asignados todavía.
                  </p>
                  <p className="text-gray-400 text-sm mt-1">
                    Los alumnos aparecerán aquí cuando se asignen a tus cursos.
                  </p>
                </td>
              </tr>
            ) : (
              alumnos.map((alumno) => (
                <tr
                  key={alumno.idUsuario}
                  className="hover:bg-purple-50 transition-colors duration-150"
                >
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-semibold text-gray-900">
                    {alumno.nombres}
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-700">
                    {alumno.apellidos}
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-700">{alumno.email}</div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-500">
                    {alumno.rut || "-"}
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-700">
                    {alumno.cursos?.[0]?.anioEscolar || "-"}
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-700">
                    {alumno.cursos?.[0]?.nombre || "-"}
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-700">
                    {alumno.cursos?.[0]?.colegio || "-"}
                  </div>
                </td>

                <td className="px-6 py-4 whitespace-nowrap">
                  <span
                    className={`px-3 py-1 inline-flex text-xs font-semibold rounded-full ${
                      alumno.vigente
                        ? "bg-green-100 text-green-800 border border-green-200"
                        : "bg-red-100 text-red-800 border border-red-200"
                    }`}
                  >
                    {alumno.vigente ? "Vigente" : "No Vigente"}
                  </span>
                </td>

                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex justify-center items-center gap-2">
                    <button
                      onClick={() => abrirModal(alumno, "editar")}
                      className="px-3 py-1.5 bg-gradient-to-r from-purple-600 to-purple-700 text-white rounded-lg hover:from-purple-700 hover:to-purple-800 transition-all shadow-sm hover:shadow-md flex items-center gap-1.5"
                      title="Editar alumno"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      <span className="text-xs font-medium">Editar</span>
                    </button>
                    <button
                      onClick={() => abrirModal(alumno, "eliminar")}
                      className="px-3 py-1.5 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all shadow-sm hover:shadow-md flex items-center gap-1.5"
                      title="Eliminar alumno"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      <span className="text-xs font-medium">Eliminar</span>
                    </button>
                  </div>
                </td>
              </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modalAbierto && (selectedAlumno || accion === "crear") && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={cerrarModal}
          ></div>

          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
              <div
                className={`px-6 py-4 flex items-center justify-between ${
                  accion === "eliminar"
                    ? "bg-gradient-to-r from-red-500 to-red-600"
                    : "bg-gradient-to-r from-purple-600 to-purple-700"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    {accion === "eliminar" ? (
                      <svg
                        className="w-6 h-6 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                    ) : (
                      <svg
                        className="w-6 h-6 text-white"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    )}
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      {accion === "crear"
                        ? "Crear Alumno"
                        : accion === "editar"
                        ? "Editar Alumno"
                        : "Eliminar Alumno"}
                    </h2>
                    <p
                      className={`text-sm ${
                        accion === "eliminar"
                          ? "text-red-100"
                          : "text-purple-100"
                      }`}
                    >
                      {accion === "crear"
                        ? "Registra un nuevo alumno en el sistema"
                        : accion === "editar"
                        ? "Modifica la información del alumno"
                        : "Esta acción no se puede deshacer"}
                    </p>
                  </div>
                </div>
                <button
                  onClick={cerrarModal}
                  className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              {/* Contenido del modal */}
              <div className="p-6 overflow-y-auto flex-1">
                {accion === "eliminar" && selectedAlumno && (
                  <div className="space-y-4">
                    <p className="text-gray-700 text-base">
                      ¿Estás seguro que deseas eliminar a{" "}
                      <span className="font-bold text-gray-900">
                        {selectedAlumno.nombreCompleto}
                      </span>
                      ?
                    </p>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-sm text-red-800">
                        Esta acción desactivará al estudiante pero no lo
                        eliminará definitivamente. El alumno quedará marcado
                        como no vigente.
                      </p>
                    </div>
                  </div>
                )}

                {accion === "editar" && selectedAlumno && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Nombres
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        value={selectedAlumno.nombres}
                        onChange={(e) =>
                          selectedAlumno &&
                          setSelectedAlumno({
                            ...selectedAlumno,
                            nombres: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Apellidos
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        value={selectedAlumno.apellidos}
                        onChange={(e) =>
                          selectedAlumno &&
                          setSelectedAlumno({
                            ...selectedAlumno,
                            apellidos: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        value={selectedAlumno.email}
                        onChange={(e) =>
                          selectedAlumno &&
                          setSelectedAlumno({
                            ...selectedAlumno,
                            email: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        RUT
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        value={selectedAlumno.rut || ""}
                        onChange={(e) =>
                          selectedAlumno &&
                          setSelectedAlumno({
                            ...selectedAlumno,
                            rut: e.target.value,
                          })
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Colegio
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
                        value={selectedAlumno.cursos?.[0]?.colegio || ""}
                        disabled
                        readOnly
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Año Escolar
                      </label>
                      <select
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white"
                        value={selectedAnioEscolarId || ""}
                        onChange={(e) => {
                          const anioEscolarId = e.target.value
                            ? parseInt(e.target.value)
                            : null;
                          setSelectedAnioEscolarId(anioEscolarId);
                          if (anioEscolarId) {
                            const cursosFiltrados = cursos.filter(
                              (c) => c.anioEscolarId === anioEscolarId
                            );
                            if (cursosFiltrados.length > 0) {
                              setSelectedCursoId(cursosFiltrados[0].idCurso);
                            } else {
                              setSelectedCursoId(null);
                            }
                          } else {
                            setSelectedCursoId(null);
                          }
                        }}
                      >
                        <option value="">Seleccione un año escolar</option>
                        {aniosEscolares.map((anio) => (
                          <option
                            key={anio.idAnioEscolar}
                            value={anio.idAnioEscolar}
                          >
                            {anio.nombre}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Curso
                      </label>
                      <select
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white disabled:bg-gray-50 disabled:text-gray-500"
                        value={selectedCursoId || ""}
                        onChange={(e) =>
                          setSelectedCursoId(
                            e.target.value ? parseInt(e.target.value) : null
                          )
                        }
                        disabled={!selectedAnioEscolarId}
                      >
                        <option value="">Seleccione un curso</option>
                        {(!selectedAnioEscolarId
                          ? cursos
                          : cursos.filter(
                              (c) => c.anioEscolarId === selectedAnioEscolarId
                            )
                        ).map((curso) => (
                          <option key={curso.idCurso} value={curso.idCurso}>
                            {curso.nombre} ({curso.AnioEscolar.nombre})
                          </option>
                        ))}
                        {cursos.length === 0 && (
                          <option disabled>No hay cursos disponibles</option>
                        )}
                      </select>
                    </div>

                    {selectedAlumno && !selectedAlumno.vigente && (
                      <div className="flex items-center space-x-3 pt-2">
                        <input
                          type="checkbox"
                          checked={selectedAlumno.vigente}
                          onChange={(e) => {
                            selectedAlumno &&
                              setSelectedAlumno({
                                ...selectedAlumno,
                                vigente: e.target.checked,
                              });
                          }}
                          className="w-5 h-5 text-purple-600 border-gray-300 rounded focus:ring-purple-500 cursor-pointer"
                        />
                        <label className="text-sm font-medium text-gray-700">
                          Vigente
                        </label>
                      </div>
                    )}

                    <div className="border-t border-gray-200 pt-4 mt-4">
                      {!mostrarCambiarPassword ? (
                        <button
                          type="button"
                          onClick={() => setMostrarCambiarPassword(true)}
                          className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 rounded-lg transition-colors border border-purple-200"
                        >
                          <div className="flex items-center gap-2">
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                              />
                            </svg>
                            <span>Cambiar Contraseña</span>
                          </div>
                          <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </button>
                      ) : (
                        <div className="space-y-4">
                          <div className="flex items-center justify-between">
                            <h3 className="text-sm font-semibold text-gray-700">
                              Cambiar Contraseña
                            </h3>
                            <button
                              type="button"
                              onClick={() => {
                                setMostrarCambiarPassword(false);
                                setNuevaPassword("");
                                setConfirmarPassword("");
                              }}
                              className="text-gray-400 hover:text-gray-600 transition-colors"
                              title="Cerrar"
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M6 18L18 6M6 6l12 12"
                                />
                              </svg>
                            </button>
                          </div>
                          <p className="text-xs text-gray-500">
                            Completa ambos campos. Deben coincidir y tener al
                            menos 6 caracteres.
                          </p>
                          <div className="space-y-3">
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Nueva Contraseña
                              </label>
                              <input
                                type="password"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                                value={nuevaPassword}
                                onChange={(e) =>
                                  setNuevaPassword(e.target.value)
                                }
                                placeholder="Mínimo 6 caracteres"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-2">
                                Confirmar Nueva Contraseña
                              </label>
                              <input
                                type="password"
                                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                                value={confirmarPassword}
                                onChange={(e) =>
                                  setConfirmarPassword(e.target.value)
                                }
                                placeholder="Repite la contraseña"
                              />
                              {nuevaPassword &&
                                confirmarPassword &&
                                nuevaPassword !== confirmarPassword && (
                                  <p className="text-xs text-red-600 mt-1">
                                    Las contraseñas no coinciden
                                  </p>
                                )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {accion === "crear" && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Nombres *
                      </label>
                      <input
                        type="text"
                        value={nuevoAlumno.nombres}
                        onChange={(e) =>
                          setNuevoAlumno({
                            ...nuevoAlumno,
                            nombres: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Apellidos *
                      </label>
                      <input
                        type="text"
                        value={nuevoAlumno.apellidos}
                        onChange={(e) =>
                          setNuevoAlumno({
                            ...nuevoAlumno,
                            apellidos: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        value={nuevoAlumno.email}
                        onChange={(e) =>
                          setNuevoAlumno({
                            ...nuevoAlumno,
                            email: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        RUT
                      </label>
                      <input
                        type="text"
                        value={nuevoAlumno.rut}
                        onChange={(e) =>
                          setNuevoAlumno({
                            ...nuevoAlumno,
                            rut: e.target.value,
                          })
                        }
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Año Escolar
                      </label>
                      <select
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white"
                        value={selectedAnioEscolarId || ""}
                        onChange={(e) => {
                          const anioEscolarId = e.target.value
                            ? parseInt(e.target.value)
                            : null;
                          setSelectedAnioEscolarId(anioEscolarId);
                          setNuevoAlumno({
                            ...nuevoAlumno,
                            anioEscolarId: anioEscolarId,
                            cursoId: null,
                          });
                          setSelectedCursoId(null);
                        }}
                      >
                        <option value="">Seleccione un año escolar</option>
                        {aniosEscolares.map((anio) => (
                          <option
                            key={anio.idAnioEscolar}
                            value={anio.idAnioEscolar}
                          >
                            {anio.nombre}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Curso
                      </label>
                      <select
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-colors bg-white disabled:bg-gray-50 disabled:text-gray-500"
                        value={selectedCursoId || ""}
                        onChange={(e) => {
                          const cursoId = e.target.value
                            ? parseInt(e.target.value)
                            : null;
                          setSelectedCursoId(cursoId);
                          setNuevoAlumno({
                            ...nuevoAlumno,
                            cursoId: cursoId,
                          });
                        }}
                        disabled={!selectedAnioEscolarId}
                      >
                        <option value="">Seleccione un curso</option>
                        {(!selectedAnioEscolarId
                          ? cursos
                          : cursos.filter(
                              (c) => c.anioEscolarId === selectedAnioEscolarId
                            )
                        ).map((curso) => (
                          <option key={curso.idCurso} value={curso.idCurso}>
                            {curso.nombre} ({curso.AnioEscolar.nombre})
                          </option>
                        ))}
                        {cursos.length === 0 && (
                          <option disabled>No hay cursos disponibles</option>
                        )}
                      </select>
                    </div>
                  </div>
                )}
              </div>

              {/* Footer del modal */}
              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
                <button
                  onClick={cerrarModal}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                >
                  Cancelar
                </button>
                <button
                  onClick={async () => {
                    if (accion === "crear") {
                      if (
                        !nuevoAlumno.nombres ||
                        !nuevoAlumno.apellidos ||
                        !nuevoAlumno.email
                      ) {
                        showNotification(
                          "error",
                          "Campos requeridos",
                          "Por favor completa todos los campos obligatorios."
                        );
                        return;
                      }
                      if (!nuevoAlumno.cursoId && !nuevoAlumno.anioEscolarId) {
                        showNotification(
                          "error",
                          "Año escolar o curso requerido",
                          "Debes seleccionar al menos un año escolar o un curso."
                        );
                        return;
                      }
                    }
                    if (accion !== "crear" && !selectedAlumno) return;

                    try {
                      setIsSaving(true);
                      if (accion === "crear") {
                        const response =
                          await alumnosProfesorService.createAlumno({
                            nombres: nuevoAlumno.nombres,
                            apellidos: nuevoAlumno.apellidos,
                            email: nuevoAlumno.email,
                            rut: nuevoAlumno.rut || null,
                            cursoId: nuevoAlumno.cursoId,
                            anioEscolarId: nuevoAlumno.anioEscolarId,
                            vigente: nuevoAlumno.vigente,
                          });
                        await reloadAlumnos();
                        cerrarModal();

                        if (response.data?.passwordTemporal) {
                          setAlumnoCreado({
                            nombres: response.data.nombres,
                            apellidos: response.data.apellidos,
                            email: response.data.email,
                            passwordTemporal: response.data.passwordTemporal,
                          });
                        } else {
                          showNotification(
                            "success",
                            "Alumno creado",
                            "El alumno ha sido registrado correctamente."
                          );
                        }
                      } else if (accion === "editar" && selectedAlumno) {
                        if (nuevaPassword || confirmarPassword) {
                          if (nuevaPassword.length < 6) {
                            showNotification(
                              "error",
                              "Contraseña inválida",
                              "La contraseña debe tener al menos 6 caracteres."
                            );
                            return;
                          }
                          if (nuevaPassword !== confirmarPassword) {
                            showNotification(
                              "error",
                              "Contraseñas no coinciden",
                              "Las contraseñas deben coincidir."
                            );
                            return;
                          }
                        }

                        const updateData: Partial<UpdateAlumnoEdit> & {
                          cursoId?: number;
                          anioEscolarId?: number | null;
                          nuevaPassword?: string;
                        } = {
                          nombres: selectedAlumno.nombres,
                          apellidos: selectedAlumno.apellidos,
                          email: selectedAlumno.email,
                          vigente: selectedAlumno.vigente,
                          ...(selectedAlumno.rut && {
                            rut: selectedAlumno.rut,
                          }),
                          ...(selectedCursoId && {
                            cursoId: selectedCursoId,
                          }),
                          ...(selectedAnioEscolarId !== undefined && {
                            anioEscolarId: selectedAnioEscolarId,
                          }),
                          ...(nuevaPassword && {
                            nuevaPassword: nuevaPassword,
                          }),
                        };

                        await alumnosProfesorService.updateAlumno(
                          selectedAlumno.idUsuario,
                          updateData
                        );

                        setSelectedAlumno({
                          ...selectedAlumno,
                          nombreCompleto: `${selectedAlumno.nombres} ${selectedAlumno.apellidos}`,
                        });
                        await reloadAlumnos();
                        cerrarModal();
                        showNotification(
                          "success",
                          "Alumno actualizado",
                          "Los cambios se han guardado correctamente."
                        );
                      }

                      if (accion === "eliminar" && selectedAlumno) {
                        await alumnosProfesorService.desactivarAlumno(
                          selectedAlumno.idUsuario
                        );

                        setSelectedAlumno({
                          ...selectedAlumno,
                          vigente: false,
                        });
                        await reloadAlumnos();
                        cerrarModal();
                        showNotification(
                          "success",
                          "Alumno desactivado",
                          "El alumno ha sido marcado como no vigente."
                        );
                      }
                    } catch (error: any) {
                      console.error("Error en operación:", error);
                      const errorInfo = getErrorMessage(error);
                      showNotification(
                        "error",
                        errorInfo.title,
                        errorInfo.message
                      );
                    } finally {
                      setIsSaving(false);
                    }
                  }}
                  className={`px-4 py-2 text-white rounded-lg transition-colors font-medium flex items-center gap-2 ${
                    accion === "crear" || accion === "editar"
                      ? "bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800"
                      : "bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800"
                  }`}
                  disabled={isSaving}
                >
                  {accion === "crear" ? (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                      Crear Alumno
                    </>
                  ) : accion === "editar" ? (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      Guardar Cambios
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                        />
                      </svg>
                      Confirmar Eliminación
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isSaving && (
        <div className="fixed inset-0 z-[70] bg-black bg-opacity-30 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-2xl p-8 flex flex-col items-center gap-4">
            <div className="relative w-20 h-20">
              <svg
                className="animate-spin w-20 h-20 text-purple-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-purple-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                  />
                </svg>
              </div>
            </div>
            <p className="text-lg font-semibold text-gray-700">
              Guardando cambios...
            </p>
          </div>
        </div>
      )}

      {notification.isOpen && (
        <div
          className={`fixed z-[60] max-w-sm w-full ${
            notification.type === "success"
              ? "bottom-4 right-4 animate-in slide-in-from-bottom-5"
              : "top-4 right-4 animate-in slide-in-from-top-5"
          }`}
        >
          <div
            className={`rounded-xl shadow-2xl p-4 border-l-4 ${
              notification.type === "success"
                ? "bg-green-50 border-green-500 text-green-800"
                : notification.type === "error"
                ? "bg-red-50 border-red-500 text-red-800"
                : notification.type === "warning"
                ? "bg-yellow-50 border-yellow-500 text-yellow-800"
                : "bg-blue-50 border-blue-500 text-blue-800"
            }`}
          >
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {notification.type === "success" && (
                  <svg
                    className="w-6 h-6 text-green-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                )}
                {notification.type === "error" && (
                  <svg
                    className="w-6 h-6 text-red-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                )}
                {notification.type === "warning" && (
                  <svg
                    className="w-6 h-6 text-yellow-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                    />
                  </svg>
                )}
                {notification.type === "info" && (
                  <svg
                    className="w-6 h-6 text-blue-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                )}
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-sm font-semibold">{notification.title}</h3>
                <p className="text-sm mt-1 opacity-90">
                  {notification.message}
                </p>
              </div>
              <button
                onClick={() =>
                  setNotification({
                    isOpen: false,
                    type: "success",
                    title: "",
                    message: "",
                  })
                }
                className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-600"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      )}

      {alumnoCreado && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={cerrarModalContrasena}
          ></div>

          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
              <div className="bg-gradient-to-r from-green-500 to-green-600 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">
                      ¡Alumno creado exitosamente!
                    </h2>
                    <p className="text-green-100 text-sm">
                      Guarda esta información para comunicarla al estudiante
                    </p>
                  </div>
                </div>
                <button
                  onClick={cerrarModalContrasena}
                  className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/10 rounded-lg"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-1">
                <div className="space-y-6">
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <h3 className="text-sm font-semibold text-purple-900 mb-3">
                      Información del estudiante
                    </h3>
                    <div className="space-y-2">
                      <div>
                        <span className="text-xs font-medium text-purple-700">
                          Nombre completo:
                        </span>
                        <p className="text-sm text-gray-900 font-semibold">
                          {alumnoCreado.nombres} {alumnoCreado.apellidos}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-purple-700">
                          Email:
                        </span>
                        <p className="text-sm text-gray-900 font-semibold">
                          {alumnoCreado.email}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border-2 border-yellow-300 rounded-lg p-6">
                    <div className="flex items-center gap-2 mb-3">
                      <svg
                        className="w-5 h-5 text-yellow-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <h3 className="text-sm font-bold text-yellow-900">
                        Contraseña temporal
                      </h3>
                    </div>
                    <p className="text-xs text-yellow-800 mb-3">
                      Esta contraseña solo se mostrará una vez. Compártela con
                      el estudiante de forma segura.
                    </p>
                    <div className="flex items-center gap-3">
                      <div className="flex-1 bg-white border-2 border-yellow-400 rounded-lg px-4 py-3">
                        <code className="text-lg font-mono font-bold text-gray-900">
                          {alumnoCreado.passwordTemporal}
                        </code>
                      </div>
                      <button
                        onClick={copiarContrasena}
                        className="px-4 py-3 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg transition-colors font-medium flex items-center gap-2"
                        title="Copiar contraseña"
                      >
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                          />
                        </svg>
                        Copiar
                      </button>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-blue-900 mb-2">
                      Instrucciones para el estudiante:
                    </h4>
                    <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
                      <li>
                        Puede iniciar sesión inmediatamente con su email y esta
                        contraseña
                      </li>
                      <li>Su cuenta ya está validada y tiene acceso premium</li>
                      <li>Puede cambiar su contraseña cuando lo desee</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 px-6 py-4 flex justify-end gap-3 border-t border-gray-200">
                <button
                  className="px-6 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 transition-colors font-medium"
                  onClick={cerrarModalContrasena}
                >
                  Entendido
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
